import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useGetProductQuery,
  useGetProductsQuery,
} from "@/redux/services/menu/menu.api";
import { Product } from "@/redux/services/menu/menu.type";
import { hasMorePages, mergePage } from "@/lib/pagedList";
import { useDebounce } from "@/hooks/useDebounce";

export const PRODUCT_PICKER_PAGE_SIZE = 50;

/**
 * Catalogue dish picker data (SFRESTO-166): searches on the server (`title`
 * filter) and pages 50 at a time ("Afficher plus"), like the "Plat existant"
 * modal, instead of loading at most 200 dishes and filtering in the browser.
 *
 * Every dish seen stays known by id, and the selected dish is fetched on its
 * own when it is not on a loaded page, so `findProduct()` can still compare
 * the form's fields with the dish's (SFRESTO-122).
 */
export const useProductPicker = ({
  restaurantId,
  enabled,
  selectedId,
}: {
  restaurantId?: string;
  enabled: boolean;
  selectedId?: string;
}) => {
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search.trim(), 300);
  // Search term and page move together, so a new search never fetches a
  // later page of the new results.
  const [query, setQuery] = useState({ q: "", page: 1 });
  const [loaded, setLoaded] = useState<Product[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [known, setKnown] = useState<Map<string, Product>>(new Map());

  useEffect(() => {
    setQuery({ q: debounced, page: 1 });
  }, [debounced]);

  const { data, isFetching } = useGetProductsQuery(
    {
      restaurantId: restaurantId as string,
      q: query.q || undefined,
      page: query.page,
      limit: PRODUCT_PICKER_PAGE_SIZE,
    },
    { skip: !restaurantId || !enabled }
  );

  const remember = useCallback((products: Product[]) => {
    if (products.length === 0) return;
    setKnown((prev) => {
      const next = new Map(prev);
      products.forEach((p) => next.set(p.id, p));
      return next;
    });
  }, []);

  useEffect(() => {
    if (!data?.results) return;
    setLoaded((prev) => mergePage(prev, data.results, query.page));
    setHasMore(
      hasMorePages({
        loaded: (query.page - 1) * PRODUCT_PICKER_PAGE_SIZE + data.results.length,
        total: data.count,
        lastPageSize: data.results.length,
        pageSize: PRODUCT_PICKER_PAGE_SIZE,
      })
    );
    remember(data.results);
  }, [data, query.page, remember]);

  // `known` is filled by an effect, so also look at the page just received.
  const selectedIsKnown =
    !!selectedId &&
    (known.has(selectedId) ||
      !!data?.results.some((p) => p.id === selectedId));
  const { data: selectedProduct } = useGetProductQuery(selectedId ?? "", {
    // Only once the first page is in and the dish is not on it.
    skip: !enabled || !selectedId || selectedIsKnown || !data || isFetching,
  });
  useEffect(() => {
    if (selectedProduct) remember([selectedProduct]);
  }, [selectedProduct, remember]);

  const findProduct = useCallback(
    (id?: string | null) => (id ? known.get(id) : undefined),
    [known]
  );

  // The selected dish always shows in the list, even off the loaded pages.
  const options = useMemo(() => {
    const selected = findProduct(selectedId);
    if (selected && !loaded.some((p) => p.id === selected.id)) {
      return [selected, ...loaded];
    }
    return loaded;
  }, [loaded, selectedId, findProduct]);

  return {
    search,
    setSearch,
    options,
    hasMore,
    loadMore: () => setQuery((prev) => ({ ...prev, page: prev.page + 1 })),
    isFetching,
    findProduct,
    isKnown: (id?: string | null) => !!id && known.has(id),
  };
};

export type ProductPicker = ReturnType<typeof useProductPicker>;
